/**
 * Lint Tool - lint-code MCP tool
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod";

import { getTokenSetExport } from "../api/token.js";
import { getCredentials } from "../common/credentials.js";
import { registerTrackedToolCall } from "../logging.js";
import { lint, type Language } from "../lint/index.js";
import { ServerContext } from "../types/server.js";

export function registerLintCodeTool(
  server: McpServer,
  context: ServerContext,
) {
  if (!context.features["TREE-2686_mcp_experimental_tools"]) {
    return;
  }

  registerTrackedToolCall(
    server,
    "lint-code",
    {
      title: "Lint code against design tokens",
      description:
        "Lint CSS, SCSS, TSX, TS, JS, or JSX code against design tokens to find hardcoded values that should use tokens. IMPORTANT: Always use this tool to validate code AFTER generating CSS/SCSS/TSX/TS/JS/JSX. When violations are found, you MUST fix them by replacing hardcoded values with the suggested design tokens from the violation results before returning the code to the user. Replace only the literal value (e.g. the color hex or dimension) with the suggested token value or variable; do not add any inline comments or labels.",
      annotations: {
        title: "Lint code against design tokens",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
      inputSchema: {
        code: z.string().describe("Source code to lint"),
        language: z
          .enum(["css", "scss", "tsx", "ts", "js", "jsx"])
          .describe("Language of the source code"),
        tokenSetId: z
          .number()
          .int()
          .positive()
          .describe("Token set ID to validate against"),
        colorDistanceThreshold: z
          .number()
          .min(0)
          .max(100)
          .default(10)
          .describe(
            "Color distance threshold for suggestions (0-100, default 10)",
          ),
      },
    },
    async (
      { code, language, tokenSetId, colorDistanceThreshold },
      { authInfo },
      clientUserAgent,
    ) => {
      const credentials = await getCredentials(authInfo);
      const tokens = await getTokenSetExport(credentials, tokenSetId, clientUserAgent);

      if (tokens === null) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "Could not fetch tokens for this token set. Check that the token set ID is valid.",
            },
          ],
        };
      }

      const violations = lint(
        code,
        language as Language,
        tokens,
        colorDistanceThreshold,
      );

      const byType = violations.reduce(
        (acc, v) => {
          acc[v.type] = (acc[v.type] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      const result = {
        violations,
        tokenSetId,
        summary: {
          total: violations.length,
          byType,
        },
      };

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(result, null, 2),
            mimeType: "application/json",
          },
        ],
      };
    },
  );
}
